import ClearIcon from '@mui/icons-material/Clear';
import { Box, Chip, IconButton, TextField } from '@mui/material';
import React, { useRef, useState } from 'react';

import { MultiPasteFilterInputProps } from './MultiPasteFilterInput.props';

// Component to override behaviour of filter value field in MUI Datagrid to support pasting a list or series of values
export const MultiPasteFilterInput: React.FC<MultiPasteFilterInputProps> = ({
  item,
  type = 'string',
  applyValue,
}) => {
  const [inputValue, setInputValue] = useState('');

  const committed = Array.isArray(item.value) ? item.value : [];
  const inputRef = useRef<HTMLInputElement | null>(null);

  const commitValues = (values: string[]) => {
    const tokens = values
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => (type === 'number' ? v.replace(/[^\d]/g, '') : v));
    const cleaned = tokens.filter(Boolean);
    if (!cleaned.length) return;

    const merged = Array.from(new Set([...committed, ...cleaned]));
    applyValue({ ...item, value: merged });
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteText = event.clipboardData.getData('text');
    if (pasteText) {
      event.preventDefault();
      const splitter = type === 'string' ? /[\r\n,;]+/ : /[\r\n,;]+|\s+/;
      const values = pasteText.split(splitter);
      commitValues(values);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Commit items to list when user press Enter, space, comma etc. - based on data type
    const commitKeys = type === 'string' ? ['Enter', ',', ';'] : ['Enter', ' ', ',', ';'];
    if (commitKeys.includes(e.key)) {
      e.preventDefault();
      if (inputValue.trim()) {
        commitValues([inputValue]);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && committed.length > 0) {
      // Delete last chip with backspace
      const updated = committed.slice(0, -1);
      applyValue({ ...item, value: updated });
    }
  };

  const handleDelete = (val: string) => {
    const updated = committed.filter((v) => v !== val);
    applyValue({ ...item, value: updated });
  };

  const handleClearAll = () => {
    applyValue({ ...item, value: [] });
    setInputValue('');
    inputRef.current?.focus();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.5,
        px: 1,
        py: 0.5,
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: 1,
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
        },
      }}
    >
      {committed.map((val) => (
        <Chip
          key={val}
          label={val}
          size="small"
          onDelete={() => handleDelete(val)}
          sx={{ height: 24 }}
        />
      ))}

      <TextField
        inputRef={inputRef}
        variant="standard"
        value={inputValue}
        type={type}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={committed.length ? '' : 'Paste or type values...'}
        InputProps={{
          disableUnderline: true,
        }}
        inputProps={{
          inputMode: type === 'number' ? 'numeric' : 'text',
        }}
        sx={{
          flexGrow: 1,
          minWidth: 80,
          pr: committed.length ? '28px' : '',
          '& .MuiInputBase-input': { fontSize: 14, py: 0.5 },
        }}
      />
      {committed.length > 0 && (
        <IconButton
          size="small"
          onClick={handleClearAll}
          sx={{
            position: 'absolute',
            top: '50%',
            right: 4,
            transform: 'translateY(-50%)',
            opacity: 0.7,
            '&:hover': { opacity: 1 },
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
